import {
  Box,
  Button,
  Center,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ILocationMenuInsight } from "stores/data/operationRuleLocations/OperationRuleLocationModel";
import { useEventEditDrawer } from "./hooks/useEventEditDrawer";
import { EditEventFormValues, INSIGHT_COLOR_LIST } from "./types";
import { ToggleBlock } from "./components/ToggleBlock";
import { InsightPreferencesBlock } from "./components/InsightPreferencesBlock";
import { AlertPreferencesBlock } from "./components/AlertPreferencesBlock";
import { Step2AlertTypeConditions } from "pages/Alert/components/Step2AlertTypeConditions";
import { MonitoredLocationPreview } from "pages/Alert/components/MonitoredLocationPreview";
import { useStores } from "stores/setup/use-store";
import { ChevronDownIcon, Send } from "lucide-react";
import {
  DebouncedInput,
  DebouncedTextarea,
} from "./components/DebouncedFormFields";
import { scrollToFirstError } from "pages/Alert/util/scrollToFirstError";

interface EventEditDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  eventToEdit: ILocationMenuInsight | null;
  locationId: number | null;
  locationName: string;
  dashboardId: number;
  onEventUpdated?: () => void;
  /** True when reopening after the user confirmed a new sensor selection in FC map */
  isReturningFromSensorSelection?: boolean;
}

export const EventEditDrawer: React.FC<EventEditDrawerProps> = observer(
  ({
    isOpen,
    onClose,
    eventToEdit,
    locationId,
    locationName,
    dashboardId,
    onEventUpdated,
    isReturningFromSensorSelection,
  }) => {
    const {
      t,
      isLoading,
      isSaving,
      initialValues,
      validationSchema,
      handleSubmit,
      resetForm,
      usersTableSelected,
      setUsersTableSelected,
      alertUsers,
      selectCurrentUser,
      environmentalVariables,
      locationData,
      templateConditions,
    } = useEventEditDrawer({
      isOpen,
      onClose,
      eventToEdit,
      locationId,
      locationName,
      dashboardId,
      isReturningFromSensorSelection,
    });

    const { t: tAlert } = useTranslation("translation", {
      keyPrefix: "oceanWeatherIntelligence.insightAlert",
    });

    const toast = useToast();

    const {
      dataStores: { forecastChartPageStore, authStore },
    } = useStores();

    // Handle drawer close
    const handleDrawerClose = useCallback(() => {
      const isInSelectionMode =
        forecastChartPageStore.isAreaDrawingMode ||
        forecastChartPageStore.isPointSelectionMode;

      onClose();

      if (!isInSelectionMode) {
        resetForm();
      }
    }, [onClose, resetForm, forecastChartPageStore]);

    // Combined translation function
    const translate = useCallback(
      (key: string) => {
        const eventTranslation = t(key);
        if (
          eventTranslation !== key &&
          !eventTranslation.startsWith("event.")
        ) {
          return eventTranslation;
        }
        return tAlert(key);
      },
      [t, tAlert],
    );

    const onSubmit = async (values: EditEventFormValues) => {
      try {
        await handleSubmit(values);
        toast({
          title: t("eventUpdatedSuccess"),
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onEventUpdated?.();
        handleDrawerClose();
      } catch (error) {
        // Spec 043 §FR-018 — surface LIGHTNING_* validation errors with their
        // dedicated i18n message. Backend returns { code: 'LIGHTNING_…' } in the
        // response body; default fallback is the generic event error.
        const code =
          (error as any)?.response?.data?.code ??
          (error as any)?.response?.data?.errorCode ??
          (error as any)?.code;
        const lightningKey =
          typeof code === "string" && code.startsWith("LIGHTNING_")
            ? `oceanWeatherIntelligence.sensorAlert.errors.${code}`
            : null;
        const lightningMsg = lightningKey ? (t(lightningKey) as string) : null;
        toast({
          title:
            lightningMsg && lightningMsg !== lightningKey
              ? lightningMsg
              : t("eventUpdatedError"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };

    /**
     * When enabling Block 2 (alerts) on an event that had no alert,
     * auto-select the current user and set initial selectedUsers in Formik.
     */
    const handleBlock2Toggle = useCallback(
      (active: boolean, setFieldValue: (field: string, value: any) => void) => {
        setFieldValue("block2Active", active);
        if (active && usersTableSelected.length === 0) {
          selectCurrentUser();
          // Also sync to Formik's selectedUsers so validation passes
          const userId = authStore.userId;
          setFieldValue("selectedUsers", [userId]);
        }
      },
      [usersTableSelected, selectCurrentUser],
    );

    // When reopening after sensor selection, scroll to the sensor alert section
    // once the drawer finishes loading so the user sees the updated selection.
    useEffect(() => {
      if (isReturningFromSensorSelection && !isLoading) {
        const timer = setTimeout(() => {
          document
            .getElementById("sensor-alert-section")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 350);
        return () => clearTimeout(timer);
      }
    }, [isReturningFromSensorSelection, isLoading]);

    return (
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleDrawerClose}
        size="xl"
      >
        <Formik<EditEventFormValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          validateOnBlur={false}
          validateOnChange={false}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit: formikSubmit,
            setFieldValue,
            validateForm,
          }) => (
            <>
              <DrawerOverlay />
              <DrawerContent bg="white">
                <DrawerCloseButton color="black" />
                <DrawerHeader>
                  <Heading display="flex" size="md" color="black" gap="16px">
                    <Text mt="3px">{t("editEvent")}</Text>
                  </Heading>
                </DrawerHeader>

                <DrawerBody color="black">
                  {isLoading ? (
                    <Center h="100%">
                      <Spinner
                        speed="0.65s"
                        emptyColor="gray.300"
                        color="newBlue.500"
                        size="xl"
                      />
                    </Center>
                  ) : (
                    <Form onSubmit={formikSubmit}>
                      <Stack spacing={6}>
                        {/* Event Name + Color Selector Row */}
                        <Flex gap="32px" align="flex-end">
                          {/* Event Name */}
                          <FormControl
                            isRequired
                            isInvalid={!!errors.name && touched.name}
                            flex={1}
                          >
                            <FormLabel color="#414651" fontWeight="500">
                              {t("eventName")}
                            </FormLabel>
                            <DebouncedInput
                              name="name"
                              placeholder={t("eventNamePlaceholder") as string}
                              formikValue={values.name}
                              onSyncValue={(val) => setFieldValue("name", val)}
                              maxLength={70}
                              borderColor="#D5D7DA"
                            />
                            <FormErrorMessage mt={4}>
                              {t("thisFieldRequired")}
                            </FormErrorMessage>
                          </FormControl>

                          {/* Color Selector */}
                          <Box flexShrink={0}>
                            <Text
                              fontSize="14px"
                              fontWeight="500"
                              color="#414651"
                              lineHeight="20px"
                              mb="8px"
                            >
                              {t("colorOnDashboard")}
                            </Text>
                            <Menu>
                              <MenuButton
                                as={Box}
                                cursor="pointer"
                                bg="white"
                                border="1px solid #D5D7DA"
                                borderRadius="8px"
                                px="14px"
                                py="6px"
                                h="40px"
                                w="146px"
                                _hover={{ borderColor: "gray.400" }}
                              >
                                <Flex align="center" justify="space-between">
                                  <HStack spacing="8px">
                                    <Box
                                      w="8px"
                                      h="8px"
                                      bg={
                                        (
                                          INSIGHT_COLOR_LIST.find(
                                            (c) => c.id === values.insightColor,
                                          ) || INSIGHT_COLOR_LIST[0]
                                        ).color
                                      }
                                      borderRadius="full"
                                    />
                                    <Text
                                      fontSize="16px"
                                      fontWeight="400"
                                      color="#717680"
                                      lineHeight="24px"
                                    >
                                      {t(
                                        {
                                          1: "red",
                                          2: "yellow",
                                          3: "blue",
                                          4: "orange",
                                        }[values.insightColor || 1] as string,
                                      )}
                                    </Text>
                                  </HStack>
                                  <ChevronDownIcon size={20} color="#717680" />
                                </Flex>
                              </MenuButton>
                              <MenuList minW="146px" zIndex={10}>
                                {INSIGHT_COLOR_LIST.map((color) => (
                                  <MenuItem
                                    key={color.id}
                                    onClick={() =>
                                      setFieldValue("insightColor", color.id)
                                    }
                                  >
                                    <HStack spacing="8px">
                                      <Box
                                        w="8px"
                                        h="8px"
                                        bg={color.color}
                                        borderRadius="full"
                                      />
                                      <Text fontSize="16px" color="#717680">
                                        {t(
                                          {
                                            1: "red",
                                            2: "yellow",
                                            3: "blue",
                                            4: "orange",
                                          }[color.id] as string,
                                        )}
                                      </Text>
                                    </HStack>
                                  </MenuItem>
                                ))}
                              </MenuList>
                            </Menu>
                          </Box>
                        </Flex>

                        {/* Event Description */}
                        <FormControl>
                          <FormLabel color="#414651" fontWeight="500">
                            {t("eventDescriptionLabel")}
                          </FormLabel>
                          <DebouncedTextarea
                            name="resume"
                            placeholder={
                              t("eventDescriptionPlaceholder") as string
                            }
                            formikValue={values.resume || ""}
                            onSyncValue={(val) => setFieldValue("resume", val)}
                            maxLength={400}
                            rows={3}
                            resize="vertical"
                            borderColor="#D5D7DA"
                          />
                        </FormControl>

                        {/* Monitored Location Preview */}
                        {locationData &&
                          (locationData.station || locationData.geofence) && (
                            <MonitoredLocationPreview
                              locationName={locationData.name}
                              station={locationData.station}
                              geofence={locationData.geofence}
                              t={translate}
                            />
                          )}

                        <Divider />

                        {/* Conditions Section */}
                        <Box>
                          <Step2AlertTypeConditions
                            values={values as any}
                            errors={errors}
                            setFieldValue={setFieldValue}
                            t={translate}
                            environmentalVariables={environmentalVariables}
                            showAlertTypeSelector={true}
                            initialTemplateConditions={templateConditions}
                            restrictionType={values.restrictionType}
                            onRestrictionTypeChange={(value: boolean) => {
                              setFieldValue("restrictionType", value);
                              // When switching to Permissive, clear title/description and reset templateType
                              // so the Step3 hook doesn't re-fill them on mount
                              if (!value) {
                                setFieldValue(
                                  "templateType",
                                  "no_category",
                                  false,
                                );
                                setFieldValue("customTitle", "", false);
                                setFieldValue("customDescription", "", false);
                              }
                            }}
                          />
                        </Box>

                        <Divider />

                        {/* Block 1: Insight Preferences (no toggle, always visible) */}
                        <InsightPreferencesBlock
                          values={values as any}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                          t={translate}
                          isEditMode={true}
                        />

                        {/* Block 2: Alert Preferences */}
                        <ToggleBlock
                          title={t("wantAlerts")}
                          isActive={values.block2Active}
                          onToggle={(active) =>
                            handleBlock2Toggle(active, setFieldValue)
                          }
                          icon={<Send size={20} color="#086AA9" />}
                        >
                          <AlertPreferencesBlock
                            values={values as any}
                            errors={errors}
                            touched={touched}
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                            t={translate as (key: React.Key) => string}
                            usersTableSelected={usersTableSelected}
                            setUsersTableSelected={setUsersTableSelected}
                            alertUsers={alertUsers}
                          />
                        </ToggleBlock>
                      </Stack>
                    </Form>
                  )}
                </DrawerBody>

                <DrawerFooter borderTopWidth="1px" bg="white">
                  <Button
                    variant="ghost"
                    color="newBlue.500"
                    mr={3}
                    onClick={handleDrawerClose}
                    isDisabled={isSaving}
                  >
                    {t("cancel")}
                  </Button>
                  <Button
                    layerStyle="ocean"
                    _hover={{ layerStyle: "ocean" }}
                    onClick={async () => {
                      // Validate and show toasts for non-field errors before submitting
                      if (values.block2Active) {
                        if (
                          !values.selectedUsers ||
                          values.selectedUsers.length === 0
                        ) {
                          toast({
                            title: translate("selectRecipient"),
                            status: "error",
                            position: "top",
                            isClosable: true,
                          });
                          return;
                        }
                        if (!values.channels || values.channels.length === 0) {
                          toast({
                            title: translate("atLeastOneChannelRequired"),
                            status: "error",
                            position: "top",
                            isClosable: true,
                          });
                          return;
                        }
                      }
                      const formErrors = await validateForm();
                      if (Object.keys(formErrors).length > 0) {
                        formikSubmit();
                        const allTouched: Record<string, boolean> = {};
                        Object.keys(formErrors).forEach((key) => { allTouched[key] = true; });
                        setTimeout(() => {
                          scrollToFirstError(formErrors, allTouched);
                        }, 100);
                        return;
                      }
                      formikSubmit();
                    }}
                    isLoading={isSaving}
                  >
                    {t("save")}
                  </Button>
                </DrawerFooter>
              </DrawerContent>
            </>
          )}
        </Formik>
      </Drawer>
    );
  },
);

export default EventEditDrawer;
